import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import CleanupInfoPanel from '../Cleanup/CleanupInfoPanel';
import { toast } from 'react-toastify';
import useResource from '../../hooks/useResource';
import RefreshIcon from '@mui/icons-material/Refresh';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const capacityResourceCache = new Map();

const formatDate = value => (value ? dayjs(value).format('DD.MM.YYYY') : 'Unbekannt');

const formatCacheEntry = data => {
  if (!data) return '—';
  if (Array.isArray(data) || typeof data !== 'object') {
    return JSON.stringify(data);
  }
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

const extractBaseResourceId = resourceId => {
  if (!resourceId) return null;
  const colonIndex = resourceId.indexOf(':');
  if (colonIndex === -1) return resourceId;
  return resourceId.substring(0, colonIndex);
};

const extractResourceSuffix = resourceId => {
  if (!resourceId) return null;
  const colonIndex = resourceId.indexOf(':');
  if (colonIndex === -1) return null;
  return resourceId.substring(colonIndex + 1);
};

const resolveResourceName = (entry, resourceMap = {}) => {
  if (!entry?.resourceId) {
    return 'Allgemeiner Cache';
  }

  const baseId = extractBaseResourceId(entry.resourceId);
  const suffix = extractResourceSuffix(entry.resourceId);
  const suffixLabel = suffix ? ` (${suffix.replace(/:/g, ' ')})` : '';

  const mapped = resourceMap[baseId] || resourceMap[entry.resourceId];
  if (mapped) {
    return `${mapped}${suffixLabel}`;
  }

  const candidate =
    entry.resourceName ||
    entry.resourceLabel ||
    entry.resourceDisplayName ||
    entry.entertainmentReservationResource?.resourceName ||
    entry.resource?.longName ||
    entry.resource?.label ||
    entry.resource?.name ||
    entry.resource?.title ||
    entry.resource?.shortName;

  return candidate ? `${candidate}${suffixLabel}` : `Resource ${entry.resourceId}`;
};

const deriveResourceLabel = resource => {
  if (!resource) {
    return null;
  }

  const label =
    resource.longName ||
    resource.name ||
    resource.label ||
    resource.displayName ||
    resource.shortName ||
    resource.resourceName ||
    resource.internalName ||
    resource.title ||
    resource.identifier ||
    resource.resourceId ||
    resource._id ||
    null;

  return label ? String(label) : null;
};

const buildCacheRows = rawData => {
  if (!rawData || Array.isArray(rawData) || typeof rawData !== 'object') {
    return {
      rows: null,
      hasDateKeys: false,
    };
  }

  const entries = Object.entries(rawData);
  const enhanced = entries.map(([key, value]) => {
    const parsed = dayjs(key);
    const isDate = parsed.isValid();
    const label = isDate ? parsed.format('DD.MM.YYYY HH:mm') : key;
    let formattedValue;
    if (value === null || value === undefined) {
      formattedValue = '—';
    } else if (typeof value === 'number') {
      formattedValue = value.toLocaleString('de-DE');
    } else if (typeof value === 'object') {
      formattedValue = JSON.stringify(value);
    } else {
      formattedValue = String(value);
    }
    return {
      key,
      label,
      value: formattedValue,
      isDate,
      sortKey: isDate ? parsed.valueOf() : key,
    };
  });

  enhanced.sort((a, b) => {
    if (a.isDate && b.isDate) {
      return a.sortKey - b.sortKey;
    }
    if (a.isDate) return -1;
    if (b.isDate) return 1;
    return String(a.sortKey).localeCompare(String(b.sortKey));
  });

  return {
    rows: enhanced,
    hasDateKeys: enhanced.some(item => item.isDate),
  };
};

export default function CapacityCacheDayContent({
  selectedEntertainmentId,
  selectedDate,
  fetchDayCache,
  lastClearAllResult,
  lastDayActionResult,
  lastClearEntertainmentResult,
}) {
  const { getResourcesByEntertainmentId, getResourceById } = useResource();
  const hasSelection = Boolean(selectedEntertainmentId && selectedDate);
  const [dayCache, setDayCache] = useState(null);
  const [resourceMap, setResourceMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchDayCacheRef = useRef(fetchDayCache);
  const getResourcesByEntertainmentIdRef = useRef(getResourcesByEntertainmentId);
  const getResourceByIdRef = useRef(getResourceById);
  const fetchedResourceIdsRef = useRef(new Set());

  useEffect(() => {
    fetchDayCacheRef.current = fetchDayCache;
  }, [fetchDayCache]);

  useEffect(() => {
    getResourcesByEntertainmentIdRef.current = getResourcesByEntertainmentId;
  });

  useEffect(() => {
    getResourceByIdRef.current = getResourceById;
  });

  const loadCache = useCallback(async () => {
    if (!hasSelection) {
      setDayCache(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetchDayCacheRef.current({
        entertainmentId: selectedEntertainmentId,
        date: selectedDate,
      });
      setDayCache(response);
    } catch (err) {
      toast.error('Fehler beim Laden des Cache-Inhalts.');
      setDayCache(null);
    } finally {
      setIsLoading(false);
    }
  }, [hasSelection, selectedEntertainmentId, selectedDate]);

  useEffect(() => {
    loadCache();
  }, [loadCache]);

  useEffect(() => {
    if (!dayCache?.caches || dayCache.caches.length === 0) {
      return;
    }

    const missingBaseIds = [
      ...new Set(
        dayCache.caches
          .map(entry => extractBaseResourceId(entry.resourceId))
          .filter(Boolean)
          .filter(baseId => !resourceMap[baseId] && !fetchedResourceIdsRef.current.has(baseId))
      ),
    ];

    if (missingBaseIds.length === 0) {
      return;
    }

    let active = true;

    const loadMissingResources = async () => {
      let hadError = false;
      try {
        const responses = await Promise.all(
          missingBaseIds.map(async baseId => {
            fetchedResourceIdsRef.current.add(baseId);
            try {
              return await getResourceByIdRef.current(baseId);
            } catch (error) {
              fetchedResourceIdsRef.current.delete(baseId);
              hadError = true;
              console.error(`Failed to load resource ${baseId} for capacity cache view`, error);
              return null;
            }
          })
        );

        if (!active) {
          return;
        }

        const mapped = responses.reduce((acc, resource) => {
          if (!resource) {
            return acc;
          }
          const resourceData = resource.resource || resource.data || resource;
          const resourceId =
            resourceData?._id ||
            resourceData?.resourceId ||
            resourceData?.id ||
            null;

          if (!resourceId) {
            return acc;
          }

          const label = deriveResourceLabel(resourceData) || resourceId;
          acc[resourceId] = label;
          return acc;
        }, {});

        if (Object.keys(mapped).length === 0) {
          if (hadError && active) {
            toast.error('Fehler beim Laden der Ressourcendetails.');
          }
          return;
        }

        setResourceMap(prev => {
          const next = { ...prev, ...mapped };
          if (selectedEntertainmentId) {
            const cached = capacityResourceCache.get(selectedEntertainmentId) || {};
            capacityResourceCache.set(selectedEntertainmentId, { ...cached, ...mapped });
          }
          return next;
        });
        if (hadError && active) {
          toast.error('Fehler beim Laden der Ressourcendetails.');
        }
      } catch (error) {
        console.error('Failed to load missing resources for capacity cache view', error);
        if (active) {
          toast.error('Fehler beim Laden der Ressourcendetails.');
        }
      }
    };

    loadMissingResources();

    return () => {
      active = false;
    };
  }, [dayCache, resourceMap, selectedEntertainmentId]);

  useEffect(() => {
    if (
      !lastClearEntertainmentResult?.selection?.entertainmentId ||
      lastClearEntertainmentResult.selection.entertainmentId !== selectedEntertainmentId
    ) {
      return;
    }
    loadCache().catch(() => {});
  }, [lastClearEntertainmentResult, selectedEntertainmentId, loadCache]);

  useEffect(() => {
    let active = true;
    fetchedResourceIdsRef.current = new Set();

    if (!selectedEntertainmentId) {
      setResourceMap({});
      return () => {
        active = false;
      };
    }

    if (capacityResourceCache.has(selectedEntertainmentId)) {
      const cached = capacityResourceCache.get(selectedEntertainmentId);
      setResourceMap(cached);
      fetchedResourceIdsRef.current = new Set(Object.keys(cached));
      return () => {
        active = false;
      };
    }

    const loadResources = async () => {
      try {
        const response = await getResourcesByEntertainmentIdRef.current(selectedEntertainmentId);
        const list = Array.isArray(response)
          ? response
          : response?.resources || response?.data || [];
        const mapped = list.reduce((acc, resource) => {
          if (!resource?._id) {
            return acc;
          }
          const label = deriveResourceLabel(resource);
          acc[resource._id] = label || resource._id;
          return acc;
        }, {});
        if (active) {
          capacityResourceCache.set(selectedEntertainmentId, mapped);
          setResourceMap(mapped);
          fetchedResourceIdsRef.current = new Set(Object.keys(mapped));
        }
      } catch (error) {
        if (active) {
          setResourceMap({});
          fetchedResourceIdsRef.current = new Set();
        }
        console.error('Failed to load resources for capacity cache view', error);
      }
    };

    loadResources();

    return () => {
      active = false;
    };
  }, [selectedEntertainmentId]);

  const handleRefresh = useCallback(() => {
    loadCache().catch(() => {});
  }, [loadCache]);

  const cacheEntries = useMemo(() => {
    if (!dayCache?.caches || dayCache.caches.length === 0) {
      return null;
    }
    return dayCache.caches.map(entry => (
      <Stack key={entry.key} spacing={1} sx={{ py: 1 }}>
        <Typography variant='subtitle2'>
          {resolveResourceName(entry, resourceMap)}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Schlüssel: {entry.key}
        </Typography>
        {(() => {
          const { rows, hasDateKeys } = buildCacheRows(entry.data);
          if (!rows) {
            return (
              <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                {formatCacheEntry(entry.data)}
              </Typography>
            );
          }
          return (
            <TableContainer
              sx={{
                borderRadius: 1,
                border: theme => `1px solid ${theme.palette.divider}`,
                maxHeight: 280,
              }}
            >
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '55%' }}>
                      {hasDateKeys ? 'Zeitpunkt' : 'Schlüssel'}
                    </TableCell>
                    <TableCell>Wert</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.key} hover sx={{ '&:last-of-type td': { border: 0 } }}>
                      <TableCell
                        sx={{
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: row.isDate ? 'tabular-nums' : 'normal',
                        }}
                      >
                        {row.label}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}
      </Stack>
    ));
  }, [dayCache, resourceMap]);

  const daySelection = lastDayActionResult?.selection;
  const dayResult = lastDayActionResult?.response?.result || null;
  const dayActionMeta = lastDayActionResult?.meta || null;
  const hasCacheEntries = Boolean(dayCache?.caches && dayCache.caches.length > 0);

  const entertainmentClearMessage = useMemo(() => {
    const selection = lastClearEntertainmentResult?.selection;
    if (!selection || selection.entertainmentId !== selectedEntertainmentId) {
      return null;
    }
    const result = lastClearEntertainmentResult?.response?.result;
    const clearedCount =
      typeof result?.clearedKeys === 'number'
        ? result.clearedKeys.toLocaleString('de-DE')
        : null;
    const clearedText = clearedCount ? ` (${clearedCount} Einträge entfernt)` : '';
    return `Cache für ${selection.entertainmentName || 'dieses Entertainment'} vollständig geleert${clearedText}.`;
  }, [lastClearEntertainmentResult, selectedEntertainmentId]);

  const allClearMessage = useMemo(() => {
    if (!lastClearAllResult) return null;
    if (lastClearAllResult.message) return lastClearAllResult.message;
    const clearedText =
      lastClearAllResult.clearedKeys !== undefined
        ? ` (${lastClearAllResult.clearedKeys} Einträge entfernt)`
        : '';
    return `Kapazitäts-Cache vollständig geleert${clearedText}.`;
  }, [lastClearAllResult]);

  const dayResultDetails = useMemo(() => {
    if (!dayResult && !dayActionMeta) {
      return null;
    }
    const details = [];
    if (dayResult && typeof dayResult.success === 'boolean') {
      details.push({
        label: 'Status',
        value: dayResult.success ? 'Erfolgreich' : 'Fehlgeschlagen',
      });
    }
    if (dayResult?.message) {
      details.push({ label: 'Nachricht', value: dayResult.message });
    }
    if (dayResult?.date) {
      details.push({ label: 'Datum', value: formatDate(dayResult.date) });
    }
    if (Array.isArray(dayResult?.clearedResources)) {
      details.push({
        label: 'Bereinigte Ressourcen',
        value: dayResult.clearedResources.length.toLocaleString('de-DE'),
      });
    }
    if (typeof dayResult?.rebuildScheduled === 'boolean') {
      details.push({
        label: 'Neuaufbau geplant',
        value: dayResult.rebuildScheduled ? 'Ja' : 'Nein',
      });
    }
    if (dayActionMeta?.action === 'rebuild-month') {
      if (dayActionMeta.startDate && dayActionMeta.endDate) {
        details.push({
          label: 'Zeitraum',
          value: `${formatDate(dayActionMeta.startDate)} – ${formatDate(dayActionMeta.endDate)}`,
        });
      }
      const processedCount = dayResult?.results?.processedDates?.length;
      if (typeof processedCount === 'number') {
        details.push({
          label: 'Verarbeitete Tage',
          value: processedCount.toLocaleString('de-DE'),
        });
      }
      const errorCount = dayResult?.results?.errors?.length;
      if (typeof errorCount === 'number' && errorCount > 0) {
        details.push({
          label: 'Fehlgeschlagene Tage',
          value: errorCount.toLocaleString('de-DE'),
        });
      }
    }
    return details.length > 0 ? details : null;
  }, [dayResult, dayActionMeta]);

  const hasVisibleContent =
    hasCacheEntries ||
    Boolean(allClearMessage) ||
    Boolean(entertainmentClearMessage) ||
    Boolean(daySelection) ||
    Boolean(dayResult);

  if (!hasSelection) {
    return null;
  }

  const refreshAction = (
    <Button
      variant='outlined'
      startIcon={isLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
      onClick={handleRefresh}
      disabled={!hasSelection || isLoading}
    >
      {isLoading ? 'Aktualisiere…' : 'Aktualisieren'}
    </Button>
  );

  if (!hasVisibleContent) {
    return (
      <Box sx={viewRootTextPrimarySx}>
        <CleanupInfoPanel title='Cache-Einträge' action={refreshAction}>
          {isLoading ? (
            <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Keine Cache-Einträge für diesen Tag.
            </Typography>
          )}
        </CleanupInfoPanel>
      </Box>
    );
  }

  return (
    <Box sx={viewRootTextPrimarySx}>
      <CleanupInfoPanel title='Cache-Einträge' action={refreshAction}>

      {allClearMessage ? (
        <Alert severity='success' sx={{ mb: 2 }}>
          {allClearMessage}
        </Alert>
      ) : null}

      {entertainmentClearMessage ? (
        <Alert severity='success' sx={{ mb: 2 }}>
          {entertainmentClearMessage}
        </Alert>
      ) : null}

      {(() => {
        if (!daySelection || !dayActionMeta) {
          return null;
        }

        const entertainmentName = daySelection.entertainmentName || 'Unbekannt';
        const formattedDate = daySelection.date ? formatDate(daySelection.date) : null;
        let message = null;

        switch (dayActionMeta.action) {
          case 'clear-day':
            message = (
              <>
                Cache für <strong>{entertainmentName}</strong>{' '}
                {formattedDate ? (
                  <>
                    am <strong>{formattedDate}</strong>
                  </>
                ) : null}{' '}
                wurde geleert (kein Neuaufbau).
              </>
            );
            break;
          case 'rebuild-day':
            message = (
              <>
                Cache für <strong>{entertainmentName}</strong>{' '}
                {formattedDate ? (
                  <>
                    am <strong>{formattedDate}</strong>
                  </>
                ) : null}{' '}
                wurde neu aufgebaut.
              </>
            );
            break;
          case 'rebuild-month': {
            const monthLabel = dayActionMeta.monthLabel || (formattedDate ? formattedDate : null);
            message = (
              <>
                Cache für <strong>{entertainmentName}</strong>{' '}
                {monthLabel ? (
                  <>
                    im Monat <strong>{monthLabel}</strong>
                  </>
                ) : null}{' '}
                wurde neu aufgebaut.
              </>
            );
            break;
          }
          default:
            message = (
              <>
                Cache-Aktion für <strong>{entertainmentName}</strong>{' '}
                {formattedDate ? (
                  <>
                    am <strong>{formattedDate}</strong>
                  </>
                ) : null}{' '}
                wurde ausgeführt.
              </>
            );
        }

        return (
          <Alert severity='success' sx={{ mb: 2 }}>
            {message}
          </Alert>
        );
      })()}

      {isLoading && !cacheEntries ? (
        <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={36} />
        </Box>
      ) : null}

      {cacheEntries ? (
        <Stack spacing={2} divider={<Divider flexItem />}>
          {cacheEntries}
        </Stack>
      ) : null}

      {dayResultDetails ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Divider textAlign='left'>
            {(() => {
              switch (dayActionMeta?.action) {
                case 'clear-day':
                  return 'Letzte Löschung';
                case 'rebuild-day':
                  return 'Letzter Neuaufbau';
                case 'rebuild-month':
                  return 'Letzte Monatsregeneration';
                default:
                  return 'Letzte Aktion';
              }
            })()}
          </Divider>
          <Stack spacing={0.5}>
            {dayResultDetails.map(item => (
              <Typography key={item.label} variant='body2'>
                <Typography component='span' variant='body2' fontWeight={600}>
                  {item.label}:
                </Typography>{' '}
                {item.value}
              </Typography>
            ))}
          </Stack>
        </Stack>
      ) : null}
    </CleanupInfoPanel>
    </Box>
  );
}

